<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { scale } from 'svelte/transition';

  import Icon, { type IconOptions } from '../icon/icon.svelte';
  import { Select, type Selected } from 'bits-ui';

  import { cn } from '$lib/utils';
  import { flyAndScale } from '$lib/transitions';
  import { reducedMotion } from '$lib/stores/reduced-motion';

  import { content, item, select } from './common';

  import type { VariantProps } from 'class-variance-authority';

  // eslint-disable-next-line no-undef
  type T = $$Generic;

  export let items: Selected<T>[];
  export let name: string | undefined = undefined;
  export let value: T;
  export let variant: VariantProps<typeof select>['variant'] | undefined = 'default';
  export let size: VariantProps<typeof select>['size'] | undefined = 'md';
  export let sameWidth = true;
  export let popoverClass: string | undefined = undefined;
  export let icon: IconOptions | undefined = undefined;
  export let disabled = false;
  export let required: boolean | undefined = undefined;
  export let placeholder: string | undefined = undefined;
  export let form: string | undefined = undefined;

  let className: string | undefined = undefined;
  export { className as class };

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  let selected: Selected<T> = items.find((item) => item.value === value)!;
  $: value = selected?.value;

  const dispatch = createEventDispatcher<{ change: Selected<T> | undefined }>();
</script>

<Select.Root
  bind:selected
  onSelectedChange={(e) => dispatch('change', e)}
  {disabled}
  {required}
  preventScroll={false}
>
  <Select.Trigger class={cn(select({ variant, size }), className)}>
    {#if icon}
      <Icon {icon} size={size || 'md'} />
    {/if}
    {#if selected}
      <span>
        {#if Array.isArray(selected)}
          {selected.map((s) => s.label).join(' / ')}
        {:else}
          {selected.label}
        {/if}
      </span>
    {/if}
    {#if placeholder && !selected}
      <Select.Value class={cn(item())} {placeholder} aria-placeholder={placeholder} />
    {/if}
    <Icon icon="chevron-down" size={size || 'md'} />
  </Select.Trigger>

  <Select.Content
    {sameWidth}
    class={cn(content(), popoverClass)}
    sideOffset={6}
    transition={flyAndScale}
    transitionConfig={$reducedMotion ? { duration: 0 } : undefined}
  >
    <Select.Group>
      {#if placeholder}
        <Select.Item disabled value="" {placeholder} label={placeholder} class={cn(item())}>
          {placeholder}
        </Select.Item>
      {/if}
      {#each items as { label, value }}
        <Select.Item let:isSelected {value} {label} class={cn(item())}>
          {label}
          {#if isSelected}
            <div transition:scale={{ duration: $reducedMotion ? 0 : 150 }}>
              <Icon icon="check" />
            </div>
          {/if}
        </Select.Item>
      {/each}
    </Select.Group>
  </Select.Content>
  <Select.Input {name} {form} />
</Select.Root>
